import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUser, FaClock, FaSignOutAlt, FaUserShield, FaUserCog, FaCode } from 'react-icons/fa';
import { Badge } from 'react-bootstrap';

const IdleTimer = ({ userName, isSidebarCollapsed, className, onLogout }) => {
    const [remainingTime, setRemainingTime] = useState(3600);
    const [showCountdown, setShowCountdown] = useState(false);
    const [showTimerView, setShowTimerView] = useState(false);
    const [userRole, setUserRole] = useState('user');
    const timeoutRef = useRef();
    const intervalRef = useRef();
    const flipIntervalRef = useRef();
    const inactivityRef = useRef(Date.now());
    const hasLoggedOutRef = useRef(false);

    const roleMap = {
        superadmin: { color: "danger", label: "Super Admin", icon: <FaUserShield /> },
        admin: { color: "primary", label: "Admin", icon: <FaUserCog /> },
        supervisor: { color: "info", label: "Supervisor", icon: <FaUser /> },
        editor: { color: "warning", label: "Editor", icon: <FaUser /> },
        viewer: { color: "secondary", label: "Viewer", icon: <FaUser /> },
        user: { color: "dark", label: "User", icon: <FaUser /> },
        developer: { color: "success", label: "Developer", icon: <FaCode /> },
    };

    useEffect(() => {
        const storedRole = localStorage.getItem('user_role') || 'user';
        setUserRole(storedRole);
    }, []);

    const getUserIcon = () => {
        const roleData = roleMap[userRole] || roleMap.user;
        return React.cloneElement(roleData.icon, { size: 14 });
    };

    const formatTime = (seconds) => {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // Detect logout when remainingTime hits 0
    useEffect(() => {
        if (remainingTime === 0 && onLogout && !hasLoggedOutRef.current) {
            hasLoggedOutRef.current = true;
            onLogout();
        }
    }, [remainingTime, onLogout]);

    const resetTimer = useCallback(() => {
        setShowCountdown(false);
        setShowTimerView(false);
        setRemainingTime(3600);
        inactivityRef.current = Date.now();
        hasLoggedOutRef.current = false;

        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (intervalRef.current) clearInterval(intervalRef.current);
        if (flipIntervalRef.current) clearInterval(flipIntervalRef.current);

        timeoutRef.current = setTimeout(() => {
            setShowCountdown(true);
            setShowTimerView(true);
        }, 5000);

        intervalRef.current = setInterval(() => {
            const elapsed = Math.floor((Date.now() - inactivityRef.current) / 1000);
            const timeLeft = Math.max(0, 3600 - elapsed);
            setRemainingTime(timeLeft);
        }, 1000);
    }, []);

    useEffect(() => {
        if (showCountdown && !flipIntervalRef.current) {
            flipIntervalRef.current = setInterval(() => {
                setShowTimerView(prev => !prev);
            }, 3000);
        }

        if (!showCountdown && flipIntervalRef.current) {
            clearInterval(flipIntervalRef.current);
            flipIntervalRef.current = null;
        }

        return () => {
            if (flipIntervalRef.current) {
                clearInterval(flipIntervalRef.current);
                flipIntervalRef.current = null;
            }
        };
    }, [showCountdown]);

    useEffect(() => {
        const events = ['keydown', 'mousedown', 'touchstart', 'scroll'];
        const handleActivity = () => resetTimer();

        events.forEach(event => document.addEventListener(event, handleActivity, true));
        resetTimer();

        return () => {
            events.forEach(event => document.removeEventListener(event, handleActivity, true));
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            if (intervalRef.current) clearInterval(intervalRef.current);
            if (flipIntervalRef.current) clearInterval(flipIntervalRef.current);
        };
    }, [resetTimer]);

    const minutesLeft = Math.floor(remainingTime / 60);
    const isWarning = showCountdown && minutesLeft <= 5;
    const isCritical = remainingTime <= 60;

    return (
        <AnimatePresence>
            {!isSidebarCollapsed && (
                <motion.div
                    className={`p-3 border-bottom position-relative ${className}`}
                    initial={{ opacity: 0, scale: 0.9, x: 20 }}
                    animate={{ opacity: 1, scale: 1, x: 0 }}
                    exit={{ opacity: 0, scale: 0.9, x: 20 }}
                    transition={{ duration: 0.2 }}
                    style={{
                        background: isWarning ? (isCritical ? "#dc3545" : "#fed7d7") : "#ced5ffff",
                        borderBottom: "1px solid #e2e8f0",
                        minHeight: '48px'
                    }}
                >
                    <AnimatePresence mode="wait">
                        {showTimerView ? (
                            <motion.div
                                key="timer"
                                initial={{ rotateY: -90, opacity: 0 }}
                                animate={{ rotateY: 0, opacity: 1 }}
                                exit={{ rotateY: 90, opacity: 0 }}
                                transition={{ duration: 0.4 }}
                                style={{ perspective: 1000 }}
                                className="w-100 h-100 d-flex align-items-center"
                            >
                                <Badge className={`fw-semibold w-100 d-flex align-items-center justify-content-between py-2 px-3 flex-grow-1 h-100 ${isWarning
                                    ? isCritical
                                        ? "bg-danger text-white border border-white shadow-lg animate-pulse"
                                        : "bg-warning text-dark border border-warning shadow"
                                    : "bg-primary text-white shadow"
                                    }`}>
                                    <FaClock size={14} className="flex-shrink-0" />
                                    <div className="flex-grow-1 mx-2" />
                                    <span className="fw-semibold lh-1 text-white flex-shrink-0">
                                        {formatTime(remainingTime)}
                                    </span>
                                    <FaSignOutAlt size={14} className="opacity-75 flex-shrink-0 ms-1" />
                                </Badge>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="user"
                                initial={{ rotateY: 90, opacity: 0 }}
                                animate={{ rotateY: 0, opacity: 1 }}
                                exit={{ rotateY: -90, opacity: 0 }}
                                transition={{ duration: 0.4 }}
                                style={{ perspective: 1000 }}
                                className="w-100 h-100 d-flex align-items-center"
                            >
                                <Badge className={`fw-semibold w-100 d-flex align-items-center justify-content-between py-2 px-3 flex-grow-1 h-100 ${showCountdown ? "bg-danger text-white shadow" : "bg-white text-muted shadow-sm"
                                    }`}>
                                    <div className="d-flex align-items-center gap-2 flex-grow-1" style={{ minWidth: 0 }}>
                                        {getUserIcon()}
                                        <span
                                            className="text-truncate"
                                            style={{
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                maxWidth: '120px'
                                            }}
                                            title={`${userName} (${roleMap[userRole]?.label || 'User'})`}
                                        >
                                            Hi, {userName}
                                        </span>
                                    </div>
                                    {showCountdown && <FaClock size={12} className="opacity-75 flex-shrink-0" />}
                                </Badge>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {isWarning && (
                        <motion.div
                            className="position-absolute top-50 end-0 translate-middle-y me-3"
                            animate={{ scale: [1, 1.3, 1], opacity: [0.6, 1, 0.6] }}
                            transition={{ duration: 1.2, repeat: Infinity }}
                            style={{
                                width: '10px', height: '10px',
                                background: isCritical ? '#ffffff' : '#dc3545',
                                borderRadius: '50%',
                                boxShadow: '0 0 12px currentColor'
                            }}
                        />
                    )}
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default IdleTimer;
